/**
 * PLATINUM — Vzorci cart limit + silent-add (v2 + v3)
 *
 * ─────────────────────────────────────────────────────────────────────
 * CHANGELOG
 * ─────────────────────────────────────────────────────────────────────
 * 2026-05-21 (v3.1 — fix discount cookie auto-apply) ← THIS DEPLOY
 *   FIXED  Discount was NOT being applied to cart, only at checkout.
 *          Root cause: ?discount=CODE on /collections/ pages is a no-op
 *          for cart-level discount application — Shopify only honors it
 *          via the /discount/CODE endpoint (which sets a session cookie).
 *   ADDED  handleSilentAdd() now fetches /discount/CODE in background BEFORE
 *          calling /cart/add.js. Browser receives Set-Cookie: discount_code=…
 *          transparently (no navigation). Cart shows 0,00 € immediately.
 *   LINK FORMAT UNCHANGED — Wisepops/Klaviyo URLs do NOT need updating.
 *
 * 2026-05-21 (v3 — silent-add via URL trigger)
 *   ADDED  Silent-add feature: URL like /collections/X?vzorci=pes|macka
 *          adds 3 configured Vzorci variants to cart silently.
 *          Customer lands on the collection page (NOT on /cart).
 *   ADDED  CONFIG constants: SILENT_ADD_PARAM, VARIANT_SETS (pes + macka)
 *   ADDED  function handleSilentAdd() — reads ?vzorci=, calls /cart/add.js,
 *          atomically sets promo flag, cleans URL params, refreshes cart UI
 *   ADDED  1 line in init() — calls handleSilentAdd()
 *   UNCHANGED  All v2 cap logic (PDP form block, reactive trim, XHR/fetch
 *              interception, toast, promo detection). Backward compatible.
 *   FILE   9 789 B → 12 405 B  (+2 616 B)
 *
 * 2026-05-10 (v2 — original deploy by previous session)
 *   Initial cap-at-3 for Vzorci when VZORCI-100 active. PDP block +
 *   reactive trim via cart endpoint interception.
 * ─────────────────────────────────────────────────────────────────────
 *
 * BEHAVIOR
 *   1. Cap Vzorci items at 3 in cart when VZORCI-100 discount is active
 *      (full-price purchases not capped).
 *   2. Silent-add via URL: ?vzorci=pes|macka pre-fills 3 variants and
 *      lands customer on whatever page the link points to (collection,
 *      product, homepage).
 *
 * Coverage:
 *   ✅ PDP "Add to Cart" form submit (prevents excess from start)
 *   ✅ /cart/add.js direct fetch calls (reactive trim)
 *   ✅ /cart/change.js direct fetch calls (cart drawer +/-, qty input)
 *   ✅ /cart/update.js direct fetch calls (bulk update)
 *   ✅ XMLHttpRequest for same endpoints (fallback for older themes)
 *   ✅ URL ?vzorci= trigger — silent add 3 configured variants (v3)
 *
 * Strategy:
 *   - PDP form: pre-validate, block if would exceed
 *   - All other cart API calls: let proceed, then reactive trim if over limit
 *   - Reactive trim = send /cart/update.js to bring count down to 3 + toast
 *   - Silent-add: idempotent (skips if cart already has MAX), URL is cleaned
 *     after handling to prevent double-trigger on refresh/back-button
 *
 * Promo detection:
 *   1. URL ?discount=VZORCI-100 → sessionStorage flag
 *   2. Cart line items with applied discount on Vzorci products
 *   3. Silent-add sets the flag atomically BEFORE the actual cart add (v3)
 */

(function () {
  'use strict';

  // === CONFIG ===
  const VZORCI_HANDLES = ['vzorci-za-pse', 'vzorci-za-macke'];
  const DISCOUNT_CODE = 'VZORCI-100';
  const MAX_VZORCI = 3;
  const PROMO_FLAG = 'platinum_vzorci_promo';
  const TRIM_DEBOUNCE_MS = 300; // Wait for batch operations to settle

  // Silent-add trigger: URL like /collections/X?vzorci=macka&discount=VZORCI-100
  //   - Shopify natively session-applies the discount code from ?discount= param
  //   - This JS picks up ?vzorci= param, adds the configured 3 variants via /cart/add.js
  //   - URL is cleaned after add to prevent double-trigger on refresh/back
  const SILENT_ADD_PARAM = 'vzorci';
  const VARIANT_SETS = {
    pes: [
      { id: 53067994759496, quantity: 1 }, // Adult Chicken
      { id: 53067995971912, quantity: 1 }, // Adult Lamb+Rice
      { id: 53067995906376, quantity: 1 }, // Adult Iberico+Greens
    ],
    macka: [
      { id: 53067994792264, quantity: 1 }, // MeatCrisp Adult Chicken (requires stock!)
      { id: 53067996234056, quantity: 1 }, // MeatCrisp Adult Fish
      { id: 53067996299592, quantity: 1 }, // MeatCrisp Sterilised Fish
    ],
  };

  // === STATE ===
  let trimInProgress = false;
  let trimTimeout = null;

  // === SAVED ORIGINALS (must be captured before any cart API patches) ===
  const originalFetch = window.fetch.bind(window);
  const OriginalXHR = window.XMLHttpRequest;

  // === HELPERS ===

  function isCartAddEndpoint(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(url);
  }

  function isCartChangeEndpoint(url) {
    return /\/cart\/(change|update)(\.js)?(\?|$)/.test(url);
  }

  function isAnyCartModifyEndpoint(url) {
    return isCartAddEndpoint(url) || isCartChangeEndpoint(url);
  }

  function detectPromoFromUrl() {
    try {
      const code = new URLSearchParams(window.location.search).get('discount');
      if (code && code.toUpperCase() === DISCOUNT_CODE.toUpperCase()) {
        sessionStorage.setItem(PROMO_FLAG, '1');
      }
    } catch (e) {}
  }

  async function fetchCart() {
    try {
      const res = await originalFetch('/cart.js', { credentials: 'same-origin' });
      return await res.json();
    } catch (e) {
      return { items: [] };
    }
  }

  async function isPromoActive() {
    try {
      if (sessionStorage.getItem(PROMO_FLAG) === '1') return true;
    } catch (e) {}

    const cart = await fetchCart();
    const hasDiscount = (cart.items || []).some(
      (item) => VZORCI_HANDLES.includes(item.handle) && item.total_discount > 0
    );
    if (hasDiscount) {
      try { sessionStorage.setItem(PROMO_FLAG, '1'); } catch (e) {}
    }
    return hasDiscount;
  }

  function getVzorciInfo(cart) {
    const lines = (cart.items || []).filter((item) =>
      VZORCI_HANDLES.includes(item.handle)
    );
    const totalQty = lines.reduce((sum, item) => sum + item.quantity, 0);
    return { lines, totalQty };
  }

  function showToast(message) {
    document.querySelectorAll('.platinum-vzorci-toast').forEach((el) => el.remove());
    const toast = document.createElement('div');
    toast.className = 'platinum-vzorci-toast';
    toast.setAttribute('role', 'alert');
    toast.style.cssText = [
      'position:fixed',
      'top:20px',
      'left:50%',
      'transform:translateX(-50%)',
      'background:#C24914',
      'color:#fff',
      'padding:14px 22px',
      'border-radius:8px',
      'box-shadow:0 6px 20px rgba(0,0,0,0.18)',
      'z-index:99999',
      'font-family:inherit',
      'font-size:15px',
      'line-height:1.4',
      'max-width:90vw',
      'text-align:center',
      'pointer-events:none',
    ].join(';');
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.transition = 'opacity 0.4s ease';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 400);
    }, 4500);
  }

  /**
   * Trigger Prestige cart UI refresh after we modify cart server-side.
   * Prestige listens to 'cart:refresh' and similar events.
   */
  function refreshCartUI() {
    // Standard Shopify section rendering API event
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
    // Prestige-specific events (best-effort)
    document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true }));
    // Try fetching cart sections (Prestige re-renders cart drawer this way)
    try {
      originalFetch('/?sections=cart-drawer,cart-icon-bubble', {
        credentials: 'same-origin',
      });
    } catch (e) {}
  }

  /**
   * Reactive trim: after a cart change, if Vzorci count > MAX, reduce to MAX.
   * Strategy: reduce the largest qty line(s) first.
   */
  async function reactiveTrim() {
    if (trimInProgress) return;
    trimInProgress = true;

    try {
      const promo = await isPromoActive();
      if (!promo) return;

      const cart = await fetchCart();
      const { lines, totalQty } = getVzorciInfo(cart);

      if (totalQty <= MAX_VZORCI) return;

      // Build updates object: variant_id → new qty
      // Strategy: sort by qty desc, reduce largest first
      const sortedLines = [...lines].sort((a, b) => b.quantity - a.quantity);
      let excess = totalQty - MAX_VZORCI;
      const updates = {};

      for (const line of sortedLines) {
        if (excess <= 0) break;
        const reduceBy = Math.min(excess, line.quantity);
        const newQty = line.quantity - reduceBy;
        updates[line.variant_id] = newQty;
        excess -= reduceBy;
      }

      // Send /cart/update.js with corrections
      await originalFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });

      showToast(
        `Lahko izbereš največ ${MAX_VZORCI} brezplačne vzorčke. Količina je bila prilagojena.`
      );

      refreshCartUI();
    } catch (e) {
      console.warn('[Platinum Vzorci] Trim failed:', e);
    } finally {
      trimInProgress = false;
    }
  }

  function scheduleTrim() {
    clearTimeout(trimTimeout);
    trimTimeout = setTimeout(reactiveTrim, TRIM_DEBOUNCE_MS);
  }

  // === LAYER 0: URL trigger silent add ===
  // Adds 3 configured Vzorci variants to cart when URL contains ?vzorci=pes|macka.
  // Atomic: sets promo flag BEFORE add so cap logic activates immediately.
  // Idempotent: skips add if cart already has MAX_VZORCI Vzorci (prevents refresh double-add).
  // URL is cleaned after handling.
  async function handleSilentAdd() {
    let url;
    try {
      url = new URL(window.location.href);
    } catch (e) {
      return;
    }

    const trigger = url.searchParams.get(SILENT_ADD_PARAM);
    if (!trigger) return;

    const items = VARIANT_SETS[trigger];
    if (!items || !items.length) {
      // Unknown trigger value — just clean URL silently
      url.searchParams.delete(SILENT_ADD_PARAM);
      try { window.history.replaceState({}, '', url.toString()); } catch (e) {}
      return;
    }

    // Atomic: set promo flag FIRST so cap logic activates immediately,
    // even if the add fails partway through.
    try { sessionStorage.setItem(PROMO_FLAG, '1'); } catch (e) {}

    try {
      // STEP 1 — Set discount cookie via /discount/ endpoint.
      // ?discount= URL param on storefront pages does NOT auto-apply to cart
      // (only at checkout, unreliably). The reliable way is /discount/CODE
      // which returns 302 + Set-Cookie: discount_code=CODE. Browser follows
      // redirect transparently in background — customer doesn't navigate away.
      try {
        await originalFetch('/discount/' + encodeURIComponent(DISCOUNT_CODE), {
          method: 'GET',
          credentials: 'same-origin',
        });
      } catch (e) {
        console.warn('[Platinum Vzorci] Discount cookie set failed:', e);
      }

      // STEP 2 — Idempotency check — if cart already has Vzorci at cap, skip add
      // to avoid pushing customer over MAX on refresh/back-button.
      const cart = await fetchCart();
      const { totalQty } = getVzorciInfo(cart);

      if (totalQty < MAX_VZORCI) {
        await originalFetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items }),
        });
      }
    } catch (e) {
      console.warn('[Platinum Vzorci] Silent add failed:', e);
    }

    // Clean URL parameter to prevent re-trigger on refresh/back.
    // Discount param (if present) is also cleaned — Shopify has already cookied it.
    url.searchParams.delete(SILENT_ADD_PARAM);
    url.searchParams.delete('discount');
    try { window.history.replaceState({}, '', url.toString()); } catch (e) {}

    refreshCartUI();
  }

  // === LAYER 1: PDP form submit pre-validation (instant block) ===

  function hookProductForms() {
    const match = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
    const handle = match ? match[1] : null;
    if (!VZORCI_HANDLES.includes(handle)) return;

    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.platinumVzorciHooked) return;
      form.dataset.platinumVzorciHooked = '1';

      form.addEventListener(
        'submit',
        async function (e) {
          const promo = await isPromoActive();
          if (!promo) return;

          const qtyInput = form.querySelector('[name="quantity"]');
          const qty = parseInt(qtyInput?.value || '1', 10) || 1;
          const cart = await fetchCart();
          const { totalQty } = getVzorciInfo(cart);

          if (totalQty + qty > MAX_VZORCI) {
            e.preventDefault();
            e.stopImmediatePropagation();
            const remaining = Math.max(0, MAX_VZORCI - totalQty);
            if (remaining === 0) {
              showToast(
                `Že imaš ${MAX_VZORCI} brezplačne vzorčke v košarici. Več v tej akciji ni mogoče.`
              );
            } else if (remaining === 1) {
              showToast(
                `Lahko izbereš še 1 brezplačen vzorček. Že imaš ${totalQty} v košarici.`
              );
            } else {
              showToast(
                `Lahko izbereš še ${remaining} brezplačna vzorčka. Že imaš ${totalQty} v košarici.`
              );
            }
          }
        },
        true
      );
    });
  }

  // === LAYER 2: fetch() interception for cart APIs (reactive trim) ===

  window.fetch = async function (...args) {
    const [resource, options] = args;
    const url =
      typeof resource === 'string'
        ? resource
        : (resource && (resource.url || resource.href)) || '';

    const result = await originalFetch.apply(this, args);

    if (isAnyCartModifyEndpoint(url) && (!options?.method || options.method.toUpperCase() === 'POST')) {
      scheduleTrim();
    }

    return result;
  };

  // === LAYER 3: XMLHttpRequest interception (fallback) ===

  window.XMLHttpRequest = function () {
    const xhr = new OriginalXHR();
    const originalOpen = xhr.open;
    let xhrUrl = '';
    let xhrMethod = '';

    xhr.open = function (method, url) {
      xhrMethod = (method || '').toUpperCase();
      xhrUrl = url || '';
      return originalOpen.apply(this, arguments);
    };

    xhr.addEventListener('load', function () {
      if (isAnyCartModifyEndpoint(xhrUrl) && xhrMethod === 'POST') {
        scheduleTrim();
      }
    });

    return xhr;
  };
  window.XMLHttpRequest.prototype = OriginalXHR.prototype;

  // === INIT ===

  function init() {
    detectPromoFromUrl();
    handleSilentAdd(); // fire-and-forget; cap logic is independent
    hookProductForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', hookProductForms);
  document.addEventListener('shopify:section:reorder', hookProductForms);
})();
